import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const CLIENTS_API = '/api/clients';
const MASTER_DATA_API = '/api/master-data';
const UPLOAD_API = '/api/api/upload';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    building: 'M3 21h18M3 7v1a3 3 0 006 0V4m0 4a3 3 0 006 0V4m0 4a3 3 0 006 0V7H3',
    user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 7a4 4 0 100-8 4 4 0 000 8z',
    phone: 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z',
    mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6',
    mapPin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z M12 13a3 3 0 100-6 3 3 0 000 6z',
    briefcase: 'M16 21v-2a2 2 0 0 0-2-2H10a2 2 0 0 0-2 2v2 M3 21h18 M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2 M3 7h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z',
    chevronLeft: 'M15 18l-6-6 6-6',
    users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
    layout: 'M3 9h18M9 21V9M3 3h18a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6',
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z M7 11V7a5 5 0 0110 0v4',
    eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z',
    eyeOff: 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94 M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19 M1 1l22 22',
};

export default function ClientDetails({ clientId, onBack }) {
    const [client, setClient] = useState(null);
    const [industries, setIndustries] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('basic');
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    // Contact management state
    const [contactView, setContactView] = useState('list'); // 'list' or 'form'
    const [contactForm, setContactForm] = useState({});
    const [editingContactIdx, setEditingContactIdx] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Document management state
    const [documentTypes, setDocumentTypes] = useState([]);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [documentForm, setDocumentForm] = useState({});
    const [editingDocumentIdx, setEditingDocumentIdx] = useState(null);

    // Refs for Autocomplete
    const basicWorkLocationRef = useRef(null);
    const modalHomeLocationRef = useRef(null);
    const modalWorkLocationRef = useRef(null);
    const autocompleteInstances = useRef({});
    const contactsFileInputRef = useRef(null);
    const [importingContacts, setImportingContacts] = useState(false);

    // Table pagination & search
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const [cRes, iRes, cnRes] = await Promise.all([
                    axios.get(`${CLIENTS_API}/all`),
                    axios.get(MASTER_DATA_API),
                    axios.get('/api/countries')
                ]);
                const found = cRes.data.find(c => c._id === clientId);
                if (found) {
                    setClient(found);
                    setForm(found);
                }
                setIndustries(iRes.data.filter(i => i.category === 'client-industry'));
                setDocumentTypes(iRes.data.filter(i => i.category === 'document-types'));
                setCountries(cnRes.data);
            } catch (err) {
                console.error("Load error:", err);
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [clientId]);

    useEffect(() => {
        if (!window.google || loading) return;

        // Basic Tab Autocomplete
        if (activeTab === 'basic' && basicWorkLocationRef.current) {
            const basicAutocomplete = new window.google.maps.places.Autocomplete(basicWorkLocationRef.current);
            basicAutocomplete.addListener('place_changed', () => {
                const place = basicAutocomplete.getPlace();
                if (place.formatted_address) {
                    setForm(prev => ({ ...prev, workLocation: place.formatted_address }));
                }
            });
        }

        // Modal Autocomplete
        if (contactView === 'form' && modalHomeLocationRef.current && modalWorkLocationRef.current) {
            const homeAuto = new window.google.maps.places.Autocomplete(modalHomeLocationRef.current);
            const workAuto = new window.google.maps.places.Autocomplete(modalWorkLocationRef.current);

            homeAuto.addListener('place_changed', () => {
                const p = homeAuto.getPlace();
                if (p.formatted_address) setContactForm(prev => ({ ...prev, homeLocation: p.formatted_address }));
            });
            workAuto.addListener('place_changed', () => {
                const p = workAuto.getPlace();
                if (p.formatted_address) setContactForm(prev => ({ ...prev, workLocation: p.formatted_address }));
            });
        }
    }, [loading, activeTab, contactView]);

    const handleUpdate = async (e) => {
        if (e) e.preventDefault();
        setSaving(true);

        try {
            let newContacts = form.contacts || [];

            if (form.workLocation) {
                newContacts = newContacts.map(c => ({
                    ...c,
                    workLocation: form.workLocation
                }));
            }

            const newForm = { ...form, contacts: newContacts };

            const { _id, __v, createdAt, updatedAt, ...sanitized } = newForm;

            if (sanitized.password === '') {
                delete sanitized.password;
            }

            // ✅ ADD THIS LINE
            console.log("Sending data:", sanitized);

            await axios.put(`${CLIENTS_API}/${clientId}`, sanitized);

            setClient(newForm);
            setForm(newForm);
            showToast('Client updated and locations synced');

        } catch (err) {
            showToast('Error updating client', 'error');
        } finally {
            setSaving(false);
        }
    };

    const openContactModal = (contact = null, idx = null) => {
        if (contact) {
            setContactForm({ ...contact });
            setEditingContactIdx(idx);
        } else {
            setContactForm({
                firstName: '', lastName: '', email: '',
                primaryPhoneCode: '', primaryPhoneNumber: '',
                alternatePhoneCode: '', alternatePhoneNumber: '',
                profileImage: '', homeLocation: '', workLocation: (form.workLocation || ''), gender: '', documents: []
            });
            setEditingContactIdx(null);
        }
        setContactView('form');
    };

    const handleDeleteContact = async (idx) => {
        if (!window.confirm('Are you sure you want to delete this contact person?')) return;
        setSaving(true);
        try {
            const newContacts = [...(form.contacts || [])];
            newContacts.splice(idx, 1);
            const newForm = { ...form, contacts: newContacts };

            const { _id, __v, createdAt, updatedAt, ...sanitized } = newForm;
            await axios.put(`${CLIENTS_API}/${clientId}`, sanitized);
            setForm(newForm);
            setClient(newForm);
            showToast('Contact removed successfully');
        } catch (err) {
            console.error("Delete contact error:", err);
            showToast('Error updating contacts', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleContactFileChange = async (e, type) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('document', files[0]);

        try {
            const res = await axios.post(UPLOAD_API, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setContactForm(prev => ({ ...prev, profileImage: res.data.filename }));
            showToast('Profile image uploaded');
        } catch (err) {
            console.error("Upload error:", err);
            showToast('Upload failed. Please try again.', 'error');
        } finally {
            setUploading(false);
        }
    };

    const saveContact = async () => {
        if (!contactForm.firstName) {
            showToast('First name is required', 'error');
            return;
        }

        setSaving(true);
        try {
            const newContacts = [...(form.contacts || [])];
            if (editingContactIdx !== null) {
                newContacts[editingContactIdx] = contactForm;
            } else {
                newContacts.push(contactForm);
            }
            const newForm = { ...form, contacts: newContacts };

            const { _id, __v, createdAt, updatedAt, ...sanitized } = newForm;
            await axios.put(`${CLIENTS_API}/${clientId}`, sanitized);

            setForm(newForm);
            setClient(newForm);
            setContactView('list');
            showToast(editingContactIdx !== null ? 'Contact updated' : 'Contact added');
        } catch (err) {
            console.error("Save contact error:", err);
            showToast('Error saving changes', 'error');
        } finally {
            setSaving(false);
        }
    };

    const downloadContactsTemplate = () => {
        const templateData = [
            {
                'First Name': 'John',
                'Last Name': 'Doe',
                'Email': 'john.doe@example.com',
                'Phone Code': '+971',
                'Phone Number': '501122334',
                'Alt Phone Code': '+1',
                'Alt Phone Number': '123456789',
                'Gender': 'Male',
                'Home Location': 'Dubai Marina',
                'Work Location': form.workLocation || 'Business Bay'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
        XLSX.writeFile(wb, `${client?.companyName || 'Client'}_Contacts_Template.xlsx`);
    };

    const handleImportContacts = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImportingContacts(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const newContacts = data.map(row => ({
                    firstName: row['First Name'],
                    lastName: row['Last Name'],
                    email: row['Email'],
                    primaryPhoneCode: row['Phone Code'] || '+971',
                    primaryPhoneNumber: row['Phone Number'],
                    alternatePhoneCode: row['Alt Phone Code'],
                    alternatePhoneNumber: row['Alt Phone Number'],
                    gender: row['Gender'],
                    homeLocation: row['Home Location'],
                    workLocation: row['Work Location'] || form.workLocation
                }));

                const updatedContacts = [...(form.contacts || []), ...newContacts];
                const newForm = { ...form, contacts: updatedContacts };

                const { _id, __v, createdAt, updatedAt, ...sanitized } = newForm;
                await axios.put(`${CLIENTS_API}/${clientId}`, sanitized);

                setForm(newForm);
                setClient(newForm);
                showToast(`${newContacts.length} contacts imported successfully!`);
            } catch (err) {
                console.error("Import error:", err);
                showToast('Import failed. Check file format.', 'error');
            } finally {
                setImportingContacts(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    // Document methods
    const openDocumentModal = (doc = null, idx = null) => {
        if (doc) {
            setDocumentForm({ ...doc });
            setEditingDocumentIdx(idx);
        } else {
            setDocumentForm({ type: '', title: '', fileName: '', expiryDate: '' });
            setEditingDocumentIdx(null);
        }
        setShowDocumentModal(true);
    };

    const handleDocumentFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('document', file);

        try {
            const res = await axios.post(UPLOAD_API, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setDocumentForm(prev => ({ ...prev, fileName: res.data.filename }));
            showToast('Document uploaded successfully');
        } catch (err) {
            showToast('Document upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    const saveDocument = async () => {
        if (!documentForm.type || !documentForm.fileName) {
            showToast('Type and file are required', 'error');
            return;
        }

        setSaving(true);
        try {
            const newDocs = [...(form.documents || [])];
            if (editingDocumentIdx !== null) {
                newDocs[editingDocumentIdx] = documentForm;
            } else {
                newDocs.push(documentForm);
            }
            const newForm = { ...form, documents: newDocs };

            const { _id, __v, createdAt, updatedAt, ...sanitized } = newForm;
            await axios.put(`${CLIENTS_API}/${clientId}`, sanitized);

            setForm(newForm);
            setClient(newForm);
            setShowDocumentModal(false);
            showToast(editingDocumentIdx !== null ? 'Document updated' : 'Document added');
        } catch (err) {
            console.error("Save document error:", err);
            showToast('Error saving document', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteDocument = async (idx) => {
        if (!window.confirm('Delete this document?')) return;
        setSaving(true);
        try {
            const newDocs = [...(form.documents || [])];
            newDocs.splice(idx, 1);
            const newForm = { ...form, documents: newDocs };

            const { _id, __v, createdAt, updatedAt, ...sanitized } = newForm;
            await axios.put(`${CLIENTS_API}/${clientId}`, sanitized);
            setForm(newForm);
            setClient(newForm);
            showToast('Document deleted');
        } catch (err) {
            showToast('Error deleting document', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ts-container">
            <style>{`
                .ts-container { display: flex; min-height: 100vh; background: #f8fafc; font-family: 'Inter', sans-serif; width: 100%; }
                .ts-sidebar { width: 300px; background: white; border-right: 1px solid #e2e8f0; display: flex; flex-direction: column; flex-shrink: 0; box-shadow: 1px 0 10px rgba(0,0,0,0.02); }
                .ts-main { flex: 1; padding: 40px; overflow-y: auto; }
                
                .ts-nav-item { 
                    padding: 14px 24px; display: flex; align-items: center; gap: 14px; cursor: pointer;
                    color: #64748b; font-weight: 600; font-size: 14px; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    border-left: 4px solid transparent; margin: 4px 0;
                }
                .ts-nav-item:hover { background: #f8fafc; color: #1e293b; padding-left: 28px; }
                .ts-nav-item.active { background: #eff6ff; color: #1e1b4b; border-left-color: #1e1b4b; }
                
                .ts-header { margin-bottom: 32px; display: flex; justify-content: space-between; align-items: flex-start; }
                .ts-title-group h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; letter-spacing: -0.02em; }
                .ts-title-group p { font-size: 14px; color: #64748b; margin-top: 6px; font-weight: 500; }

                .ts-content-card { background: white; border-radius: 24px; border: 1px solid #e2e8f0; padding: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.02); }

                .cd-form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 28px; max-width: 900px; }
                .cd-form-group { display: flex; flex-direction: column; gap: 8px; }
                .cd-form-group.full { grid-column: span 2; }
                .cd-form-group label { font-size: 13px; font-weight: 700; color: #475569; margin-left: 4px; }
                
                .cd-input-wrap { position: relative; display: flex; align-items: center; }
                .cd-input-icon { position: absolute; left: 16px; color: #94a3b8; pointer-events: none; transition: 0.2s; }
                .cd-input, .cd-select, .cd-textarea {
                    width: 100%; padding: 12px 16px 12px 48px; border: 2px solid #f1f5f9;
                    border-radius: 12px; font-size: 14px; color: #1e293b; background: #f8fafc;
                    outline: none; transition: all 0.2s; font-family: 'Inter', sans-serif; font-weight: 600;
                    box-sizing: border-box;
                }
                .cd-select { padding-left: 16px; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 16px center; background-size: 16px; }
                .cd-textarea { padding-left: 16px; min-height: 120px; resize: vertical; }

                .cd-input:focus, .cd-select:focus, .cd-textarea:focus { border-color: #1e1b4b; background: white; box-shadow: 0 0 0 4px rgba(30, 27, 75, 0.05); }
                .cd-input:focus + .cd-input-icon { color: #1e1b4b; }

                .cd-eye-btn {
                    position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
                    background: none; border: none; cursor: pointer; color: #94a3b8;
                    display: flex; align-items: center; padding: 4px; border-radius: 6px;
                    transition: color 0.2s;
                }
                .cd-eye-btn:hover { color: #1e1b4b; }

                .phone-input-pair { display: flex; gap: 10px; width: 100%; }
                .code-select { width: 110px !important; padding: 12px 14px !important; flex-shrink: 0; }
                .num-input { flex: 1; }

                .cd-btn-save {
                    padding: 12px 32px; background: #1e1b4b; color: white; border: none;
                    border-radius: 14px; font-weight: 700; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 4px 12px rgba(30, 27, 75, 0.2); font-size: 14px;
                }
                .cd-btn-save:hover { background: #2d2a6b; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(30, 27, 75, 0.3); }
                .cd-btn-save:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

                .contacts-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 24px; }
                .contact-card { 
                    background: white; border: 1px solid #e2e8f0; border-radius: 20px; padding: 24px;
                    display: flex; flex-direction: column; gap: 18px; transition: all 0.3s ease;
                    position: relative; overflow: hidden;
                }
                .contact-card:hover { transform: translateY(-4px); box-shadow: 0 15px 30px -10px rgba(0,0,0,0.08); border-color: #1e1b4b; }
                .contact-avatar { width: 64px; height: 64px; border-radius: 18px; border: 3px solid #f1f5f9; background: #1e1b4b; color: white; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 800; object-fit: cover; box-shadow: 0 4px 10px rgba(0,0,0,0.05); }
                .contact-info-main h4 { font-size: 17px; font-weight: 800; color: #1e293b; margin: 0; }
                .contact-info-main p { font-size: 13px; color: #64748b; margin: 2px 0 0 0; font-weight: 500; }
                
                .contact-meta-item { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #475569; font-weight: 500; }
                
                .doc-badge { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #f1f5f9; border-radius: 10px; font-size: 12px; color: #1e1b4b; text-decoration: none; font-weight: 700; margin-bottom: 6px; transition: 0.2s; }
                .doc-badge:hover { background: #e2e8f0; }

                .contact-actions { display: flex; gap: 10px; margin-top: auto; padding-top: 20px; border-top: 1px solid #f1f5f9; }
                .act-btn { flex: 1; padding: 10px; border-radius: 12px; border: 1.5px solid #e2e8f0; background: white; cursor: pointer; transition: 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 12px; font-weight: 700; color: #64748b; }
                .act-btn:hover { border-color: #1e1b4b; color: #1e1b4b; background: #f8fafc; }
                .act-btn.del:hover { border-color: #ef4444; color: #ef4444; background: #fff1f2; }

                .cd-toast { position: fixed; top: 24px; right: 24px; padding: 16px 28px; border-radius: 16px; color: white; z-index: 5000; font-weight: 800; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.15); animation: toastPop 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28); }
                @keyframes toastPop { from { transform: scale(0.8) translateY(20px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }
                .cd-toast.success { background: #10b981; }
                .cd-toast.error { background: #ef4444; }

                .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(10px); display: flex; align-items: center; justify-content: center; z-index: 6000; animation: modalIn 0.3s ease; }
                @keyframes modalIn { from { opacity: 0; } to { opacity: 1; } }
                .modal-card { background: white; border-radius: 32px; width: 100%; max-width: 620px; padding: 40px; box-shadow: 0 30px 60px -12px rgba(15, 23, 42, 0.25); animation: cardPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
                @keyframes cardPop { from { transform: scale(0.9) translateY(30px); } to { transform: scale(1) translateY(0); } }
                .modal-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
                .modal-form-grid .full { grid-column: span 2; }
                .contact-form-label { font-size: 13px; font-weight: 700; color: #1e293b; margin-bottom: 8px; display: block; margin-left: 2px; }
                
                .file-upload-zone {
                    border: 2px dashed #e2e8f0; border-radius: 14px; padding: 16px;
                    background: #f8fafc; transition: all 0.2s; position: relative;
                    display: flex; align-items: center; justify-content: center; gap: 12px;
                    cursor: pointer;
                }
                .file-upload-zone:hover { border-color: #1e1b4b; background: #f1f5f9; }
                .file-upload-zone input[type="file"] { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
                .file-upload-text { font-size: 13px; font-weight: 600; color: #64748b; }
                .file-upload-icon { color: #94a3b8; }

                .cd-table-container { width: 100%; overflow-x: auto; border-radius: 16px; border: 1px solid #f1f5f9; background: white; }
                .cd-table { width: 100%; border-collapse: collapse; text-align: left; }
                .cd-table th { background: #f8fafc; padding: 16px 20px; font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #f1f5f9; }
                .cd-table td { padding: 16px 20px; font-size: 13px; color: #1e293b; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .cd-table tr:last-child td { border-bottom: none; }
                .cd-table tr:hover td { background: #fbfcfe; }

                .table-avatar { width: 36px; height: 36px; border-radius: 10px; background: #1e1b4b; color: white; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 800; }
                .table-name-cell { display: flex; align-items: center; gap: 12px; font-weight: 700; color: #1e293b; }
                
                .action-pill { padding: 6px 10px; border-radius: 8px; border: none; background: #f1f5f9; color: #64748b; cursor: pointer; transition: 0.2s; display: inline-flex; align-items: center; justify-content: center; margin-right: 6px; }
                .action-pill:hover { background: #1e1b4b; color: white; }
                .action-pill.red:hover { background: #ef4444; color: white; }

                .search-bar { width: 100%; max-width: 320px; margin-bottom: 20px; position: relative; }
                .search-bar input { width: 100%; padding: 10px 16px 10px 40px; border: 2px solid #f1f5f9; border-radius: 12px; font-size: 13px; font-weight: 600; outline: none; transition: 0.2s; }
                .search-bar input:focus { border-color: #1e1b4b; background: white; }
                .search-btn-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; }

                .pagination { display: flex; align-items: center; justify-content: space-between; margin-top: 24px; padding: 0 4px; }
                .pg-info { font-size: 13px; color: #64748b; font-weight: 600; }
                .pg-actions { display: flex; gap: 8px; }
                .pg-btn { padding: 8px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: white; font-size: 12px; font-weight: 700; cursor: pointer; transition: 0.2s; color: #1e1b4b; }
                .pg-btn:hover:not(:disabled) { border-color: #1e1b4b; background: #f8fafc; }
                .pg-btn:disabled { opacity: 0.5; cursor: not-allowed; }

                /* Premium Buttons */
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-import {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                .btn-template {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                }
                .btn-primary {
                    background: #1e1b4b;
                    box-shadow: 0 4px 12px rgba(30, 27, 75, 0.25);
                }
                .premium-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }
            `}</style>

            {toast && <div className={`cd-toast ${toast.type}`}>{toast.msg}</div>}

            <aside className="ts-sidebar">
                <div style={{ padding: '32px 24px 10px' }}>
                    <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none', background: 'none', color: '#64748b', fontSize: '13px', fontWeight: 700, cursor: 'pointer', transition: '0.2s' }}>
                        <Icon d={icons.chevronLeft} size={16} strokeWidth={2.5} /> Back to Clients
                    </button>
                    <div style={{ marginTop: '24px', marginBottom: '32px' }}>
                        <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#1e293b', margin: 0 }}>{client?.companyName || 'Loading...'}</h2>
                        <p style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 800, textTransform: 'uppercase', marginTop: '6px', letterSpacing: '0.05em' }}>CLIENT PROFILE</p>
                    </div>
                </div>

                <div className="ts-nav-item active" style={{ marginBottom: 8, cursor: 'default', background: 'none', opacity: 0.5, border: 'none' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>Navigation</span>
                </div>

                <div className={`ts-nav-item ${activeTab === 'basic' ? 'active' : ''}`} onClick={() => setActiveTab('basic')}>
                    <Icon d={icons.layout} size={20} strokeWidth={2} />
                    <span>Basic Information</span>
                </div>
                <div className={`ts-nav-item ${activeTab === 'people' ? 'active' : ''}`} onClick={() => setActiveTab('people')}>
                    <Icon d={icons.users} size={20} strokeWidth={2} />
                    <span>People & Contacts</span>
                </div>
                <div className={`ts-nav-item ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>
                    <Icon d={icons.file} size={20} strokeWidth={2} />
                    <span>Documents</span>
                </div>
            </aside>

            <main className="ts-main">
                <header className="ts-header">
                    <div className="ts-title-group">
                        {activeTab === 'people' && contactView === 'form' ? (
                            <>
                                <button onClick={() => setContactView('list')} style={{ display: 'flex', alignItems: 'center', gap: '8px', border: 'none', background: 'none', color: '#64748b', fontSize: '13px', fontWeight: 700, cursor: 'pointer', transition: '0.2s', marginBottom: 16, padding: 0 }}>
                                    <Icon d={icons.chevronLeft} size={16} strokeWidth={2.5} /> Back to Contacts
                                </button>
                                <h1>{editingContactIdx !== null ? 'Update Stakeholder' : 'Register New Contact'}</h1>
                                <p>Fill in the details below to {editingContactIdx !== null ? 'update the' : 'create a new'} client contact record.</p>
                            </>
                        ) : (
                            <>
                                <h1>{activeTab === 'basic' ? 'Basic Information' : activeTab === 'people' ? 'Personnel & Contacts' : 'Corporate Documents'}</h1>
                                <p>{activeTab === 'basic' ? 'Update company details, address and primary business associations.' : activeTab === 'people' ? 'Manage client stakeholders and team members. (Note: Contacts and Passengers are now automatically synchronized across portals)' : 'Repository for official contracts, KYC, and regulatory documentation.'}</p>
                            </>
                        )}
                    </div>
                    {activeTab === 'people' && contactView === 'list' && (
                        <div style={{ display: 'flex', gap: 12 }}>
                            <input type="file" ref={contactsFileInputRef} hidden accept=".xlsx,.xls" onChange={handleImportContacts} />
                            <button className="premium-btn btn-template" onClick={downloadContactsTemplate}>
                                <Icon d={icons.download} size={18} strokeWidth={2.5} />
                                Template
                            </button>
                            <button className="premium-btn btn-import" onClick={() => contactsFileInputRef.current.click()} disabled={importingContacts}>
                                <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                                {importingContacts ? 'Importing...' : 'Import Contacts'}
                            </button>
                            <button className="premium-btn btn-primary" onClick={() => openContactModal()}>
                                <Icon d={icons.users} size={18} strokeWidth={2.5} />
                                Add New Contact
                            </button>
                        </div>
                    )}
                    {activeTab === 'documents' && (
                        <button className="premium-btn btn-primary" onClick={() => openDocumentModal()}>
                            <Icon d={icons.file} size={18} strokeWidth={2.5} />
                            Upload New Document
                        </button>
                    )}
                </header>

                <div className="ts-content-card">
                    {loading ? (
                        <div style={{ padding: '80px', textAlign: 'center', color: '#64748b' }}>
                            <div style={{ width: 40, height: 40, border: '4px solid #f1f5f9', borderTopColor: '#1e1b4b', borderRadius: '50%', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }}></div>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            Fetching client information...
                        </div>
                    ) : activeTab === 'basic' ? (
                        <form onSubmit={handleUpdate}>
                            <div className="cd-form-grid">
                                <div className="cd-form-group full">
                                    <label>Company Legal Name</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.building} size={18} className="cd-input-icon" strokeWidth={2.5} />
                                        <input className="cd-input" required value={form.companyName || ''} onChange={e => setForm({ ...form, companyName: e.target.value })} placeholder="e.g. Acme Corp" />
                                    </div>
                                </div>
                                <div className="cd-form-group">
                                    <label>Industry Sector</label>
                                    <select className="cd-select" value={form.industry || ''} onChange={e => setForm({ ...form, industry: e.target.value })}>
                                        <option value="">Select Sector</option>
                                        {industries.map(ind => <option key={ind._id} value={ind.name}>{ind.name}</option>)}
                                    </select>
                                </div>
                                <div className="cd-form-group">
                                    <label>Operational Status</label>
                                    <select className="cd-select" value={form.status || 'Active'} onChange={e => setForm({ ...form, status: e.target.value })}>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                                <div className="cd-form-group full">
                                    <label>Business Address</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.mapPin} size={18} className="cd-input-icon" strokeWidth={2.5} />
                                        <input className="cd-input" value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full office address" />
                                    </div>
                                </div>
                                <div className="cd-form-group">
                                    <label>Primary Phone Line</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.phone} size={18} className="cd-input-icon" strokeWidth={2.5} />
                                        <input className="cd-input" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+1..." />
                                    </div>
                                </div>
                                <div className="cd-form-group full">
                                    <label>Central Business Email</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.mail} size={18} className="cd-input-icon" strokeWidth={2.5} />
                                        <input className="cd-input" type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="office@company.com" />
                                    </div>
                                </div>
                                <div className="cd-form-group">
                                    <label>Access Password</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.lock} size={18} className="cd-input-icon" strokeWidth={2.5} />
                                        <input
                                            className="cd-input"
                                            type={showPassword ? 'text' : 'password'}
                                            value={form.password || ''}
                                            onChange={e => setForm({ ...form, password: e.target.value })}
                                            placeholder="Update password..."
                                            style={{ paddingRight: '44px' }}
                                        />
                                        <button
                                            type="button"
                                            className="cd-eye-btn"
                                            onClick={() => setShowPassword(v => !v)}
                                            tabIndex={-1}
                                        >
                                            <Icon d={showPassword ? icons.eyeOff : icons.eye} size={16} strokeWidth={2} />
                                        </button>
                                    </div>
                                </div>
                                <div className="cd-form-group full">
                                    <label>Main Work Hub / Head Office (Syncs to all Members)</label>
                                    <div className="cd-input-wrap">
                                        <Icon d={icons.mapPin} size={18} className="cd-input-icon" strokeWidth={2.5} />
                                        <input ref={basicWorkLocationRef} className="cd-input" value={form.workLocation || ''} onChange={e => setForm({ ...form, workLocation: e.target.value })} placeholder="Search head office location..." />
                                    </div>
                                </div>
                                <div className="cd-form-group full">
                                    <label>Account Notes & Internal Details</label>
                                    <textarea className="cd-textarea" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Strategic notes, contract references, etc." />
                                </div>
                            </div>
                            <div style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end', paddingTop: 32, borderTop: '1px solid #f1f5f9' }}>
                                <button type="submit" className="cd-btn-save" disabled={saving}>
                                    {saving ? 'Synchronizing...' : 'Update Base Profile'}
                                </button>
                            </div>
                        </form>
                    ) : activeTab === 'people' ? (
                        <div>
                            {contactView === 'list' ? (
                                (!form.contacts || form.contacts.length === 0) ? (
                                    <div style={{ padding: '80px 40px', textAlign: 'center', background: '#f8fafc', borderRadius: 28, border: '2px dashed #cbd5e1' }}>
                                        <Icon d={icons.users} size={64} color="#cbd5e1" strokeWidth={1} />
                                        <h3 style={{ margin: '24px 0 8px', color: '#1e293b' }}>No Contacts Found</h3>
                                        <p style={{ color: '#64748b', fontSize: '15px', maxWidth: 400, margin: '0 auto 24px' }}>Establish a communication network by adding key contacts for this client.</p>
                                        <button className="cd-btn-save" onClick={() => openContactModal()}>Create First Contact</button>
                                    </div>
                                ) : (() => {
                                    const filtered = form.contacts.filter(c =>
                                        `${c.firstName} ${c.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        c.email?.toLowerCase().includes(searchTerm.toLowerCase())
                                    );
                                    const totalPages = Math.ceil(filtered.length / rowsPerPage);
                                    const startIndex = (currentPage - 1) * rowsPerPage;
                                    const pagedData = filtered.slice(startIndex, startIndex + rowsPerPage);

                                    return (
                                        <>
                                            <div className="search-bar">
                                                <Icon d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" size={18} className="search-btn-icon" />
                                                <input type="text" placeholder="Search stakeholders..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                                            </div>
                                            <div className="cd-table-container">
                                                <table className="cd-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Personnel</th>
                                                            <th>Gender</th>
                                                            <th>Email Address</th>
                                                            <th>Primary Phone</th>
                                                            <th>Alt Phone</th>
                                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {pagedData.map((contact, idx) => {
                                                            const originalIdx = form.contacts.findIndex(c => c === contact);
                                                            return (
                                                                <tr key={idx}>
                                                                    <td>
                                                                        <div className="table-name-cell">
                                                                            {contact.profileImage ? (
                                                                                <img src={`/api/uploads/${contact.profileImage}`} className="table-avatar" alt="" />
                                                                            ) : (
                                                                                <div className="table-avatar">{(contact.firstName || '?').charAt(0)}</div>
                                                                            )}
                                                                            {contact.firstName} {contact.lastName}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <span style={{ 
                                                                            padding: '4px 10px', 
                                                                            borderRadius: '6px', 
                                                                            fontSize: '11px', 
                                                                            fontWeight: 700,
                                                                            background: contact.gender === 'Male' ? '#eff6ff' : contact.gender === 'Female' ? '#fdf2f8' : '#f1f5f9',
                                                                            color: contact.gender === 'Male' ? '#1d4ed8' : contact.gender === 'Female' ? '#db2777' : '#64748b'
                                                                        }}>
                                                                            {contact.gender || '—'}
                                                                        </span>
                                                                    </td>
                                                                    <td>{contact.email}</td>
                                                                    <td><span style={{ color: '#64748b', fontWeight: 600 }}>{contact.primaryPhoneCode}</span> {contact.primaryPhoneNumber}</td>
                                                                    <td><span style={{ color: '#64748b', fontWeight: 600 }}>{contact.alternatePhoneCode}</span> {contact.alternatePhoneNumber}</td>
                                                                    <td style={{ textAlign: 'right' }}>
                                                                        <button className="action-pill" onClick={() => openContactModal(contact, originalIdx)}>
                                                                            <Icon d={icons.edit} size={14} />
                                                                        </button>
                                                                        <button className="action-pill red" onClick={() => handleDeleteContact(originalIdx)}>
                                                                            <Icon d={icons.trash} size={14} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {totalPages > 1 && (
                                                <div className="pagination">
                                                    <div className="pg-info">Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, filtered.length)} of {filtered.length} personnel</div>
                                                    <div className="pg-actions">
                                                        <button className="pg-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(v => v - 1)}>Prev</button>
                                                        <button className="pg-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(v => v + 1)}>Next</button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()
                            ) : (
                                <div className="contact-form-container">
                                    <div className="modal-form-grid">
                                        <div className="full">
                                            <label className="contact-form-label">Profile Picture Attachment</label>
                                            <div className="file-upload-zone">
                                                <Icon d={icons.user} size={20} className="file-upload-icon" />
                                                <span className="file-upload-text">{contactForm.profileImage ? 'Change Image' : 'Click to Upload Avatar'}</span>
                                                <input type="file" onChange={e => handleContactFileChange(e, 'profileImage')} accept="image/*" disabled={uploading} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="contact-form-label">First Name</label>
                                            <div className="cd-input-wrap">
                                                <Icon d={icons.user} size={18} className="cd-input-icon" />
                                                <input className="cd-input" value={contactForm.firstName || ''} onChange={e => setContactForm({ ...contactForm, firstName: e.target.value })} placeholder="John" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="contact-form-label">Last Name</label>
                                            <div className="cd-input-wrap">
                                                <Icon d={icons.user} size={18} className="cd-input-icon" />
                                                <input className="cd-input" value={contactForm.lastName || ''} onChange={e => setContactForm({ ...contactForm, lastName: e.target.value })} placeholder="Doe" />
                                            </div>
                                        </div>
                                        <div className="full">
                                            <label className="contact-form-label">Direct Email Communication</label>
                                            <div className="cd-input-wrap">
                                                <Icon d={icons.mail} size={18} className="cd-input-icon" />
                                                <input className="cd-input" type="email" value={contactForm.email || ''} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} placeholder="stakeholder@company.com" />
                                            </div>
                                        </div>
                                        <div className="full">
                                            <label className="contact-form-label">Primary Communication Number</label>
                                            <div className="phone-input-pair">
                                                <select className="cd-select code-select" value={contactForm.primaryPhoneCode || ''} onChange={e => setContactForm({ ...contactForm, primaryPhoneCode: e.target.value })}>
                                                    <option value="">Code</option>
                                                    {countries.map(c => <option key={c._id} value={c.code}>{c.code} ({c.name})</option>)}
                                                </select>
                                                <div className="cd-input-wrap num-input">
                                                    <Icon d={icons.phone} size={18} className="cd-input-icon" />
                                                    <input className="cd-input" value={contactForm.primaryPhoneNumber || ''} onChange={e => setContactForm({ ...contactForm, primaryPhoneNumber: e.target.value })} placeholder="Primary Number" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="full">
                                            <label className="contact-form-label">Alternate Contact / Phone</label>
                                            <div className="phone-input-pair">
                                                <select className="cd-select code-select" value={contactForm.alternatePhoneCode || ''} onChange={e => setContactForm({ ...contactForm, alternatePhoneCode: e.target.value })}>
                                                    <option value="">Code</option>
                                                    {countries.map(c => <option key={c._id} value={c.code}>{c.code} ({c.name})</option>)}
                                                </select>
                                                <div className="cd-input-wrap num-input">
                                                    <Icon d={icons.phone} size={18} className="cd-input-icon" />
                                                    <input className="cd-input" value={contactForm.alternatePhoneNumber || ''} onChange={e => setContactForm({ ...contactForm, alternatePhoneNumber: e.target.value })} placeholder="Secondary phone" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="full">
                                            <label className="contact-form-label">Home Location (Personal)</label>
                                            <div className="cd-input-wrap">
                                                <Icon d={icons.mapPin} size={18} className="cd-input-icon" />
                                                <input ref={modalHomeLocationRef} className="cd-input" value={contactForm.homeLocation || ''} onChange={e => setContactForm({ ...contactForm, homeLocation: e.target.value })} placeholder="Personal address" />
                                            </div>
                                        </div>
                                        <div className="full">
                                            <label className="contact-form-label">Work Location (Assigned)</label>
                                            <div className="cd-input-wrap">
                                                <Icon d={icons.briefcase} size={18} className="cd-input-icon" />
                                                <input ref={modalWorkLocationRef} className="cd-input" value={contactForm.workLocation || ''} onChange={e => setContactForm({ ...contactForm, workLocation: e.target.value })} placeholder="Work site / branch Office" />
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }} className="full">
                                            <div>
                                                <label className="contact-form-label">Gender</label>
                                                <select className="cd-select" value={contactForm.gender || ''} onChange={e => setContactForm({ ...contactForm, gender: e.target.value })} style={{ paddingLeft: 16 }}>
                                                    <option value="">Select</option>
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="contact-form-label">Status</label>
                                                <select className="cd-select" value={contactForm.status || 'Active'} onChange={e => setContactForm({ ...contactForm, status: e.target.value })} style={{ paddingLeft: 16 }}>
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="contact-form-label">Passenger ID (Unique ID)</label>
                                                <div className="cd-input-wrap">
                                                    <Icon d={icons.user} size={18} className="cd-input-icon" />
                                                    <input className="cd-input" value={contactForm.employeeId || ''} onChange={e => setContactForm({ ...contactForm, employeeId: e.target.value })} placeholder="e.g. EMP12345" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end', gap: 16, paddingTop: 32, borderTop: '1px solid #f1f5f9' }}>
                                        <button className="act-btn" style={{ flex: 'none', padding: '12px 24px', fontSize: 14 }} onClick={() => setContactView('list')} disabled={saving}>Discard & Return</button>
                                        <button className="cd-btn-save" onClick={saveContact} disabled={uploading || saving}>
                                            {uploading ? 'Processing Files...' : saving ? 'Saving Changes...' : editingContactIdx !== null ? 'Save Changes' : 'Register Contact'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : activeTab === 'documents' ? (
                        <div>
                            {(!form.documents || form.documents.length === 0) ? (
                                <div style={{ padding: '80px 40px', textAlign: 'center', background: '#f8fafc', borderRadius: 28, border: '2px dashed #cbd5e1' }}>
                                    <Icon d={icons.file} size={64} color="#cbd5e1" strokeWidth={1} />
                                    <h3 style={{ margin: '24px 0 8px', color: '#1e293b' }}>No Documents Uploaded</h3>
                                    <p style={{ color: '#64748b', fontSize: '15px', maxWidth: 400, margin: '0 auto 24px' }}>Maintain a digital archive for this client by uploading contracts and legal papers.</p>
                                    <button className="cd-btn-save" onClick={() => openDocumentModal()}>Upload First Document</button>
                                </div>
                            ) : (
                                <div className="cd-table-container">
                                    <table className="cd-table">
                                        <thead>
                                            <tr>
                                                <th>Document Title</th>
                                                <th>Type</th>
                                                <th>Expiry Date</th>
                                                <th style={{ textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form.documents.map((doc, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <div className="table-name-cell">
                                                            <div className="table-avatar" style={{ background: '#e0e7ff', color: '#4338ca' }}>
                                                                <Icon d={icons.file} size={16} />
                                                            </div>
                                                            {doc.title || 'Untitled Document'}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span style={{ padding: '4px 10px', background: '#f1f5f9', borderRadius: '6px', fontSize: '12px', fontWeight: 700, color: '#475569' }}>
                                                            {doc.type}
                                                        </span>
                                                    </td>
                                                    <td>{doc.expiryDate ? new Date(doc.expiryDate).toLocaleDateString() : '—'}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <a href={`/api/uploads/${doc.fileName}`} target="_blank" rel="noopener noreferrer" className="action-pill">
                                                            <Icon d={icons.download} size={14} />
                                                        </a>
                                                        <button className="action-pill" onClick={() => openDocumentModal(doc, idx)}>
                                                            <Icon d={icons.edit} size={14} />
                                                        </button>
                                                        <button className="action-pill red" onClick={() => handleDeleteDocument(idx)}>
                                                            <Icon d={icons.trash} size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </main>

            {showDocumentModal && (
                <div className="modal-overlay">
                    <div className="modal-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                            <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{editingDocumentIdx !== null ? 'Update Document' : 'Upload Document'}</h3>
                            <button onClick={() => setShowDocumentModal(false)} style={{ border: 'none', background: '#f1f5f9', width: 36, height: 36, borderRadius: 12, cursor: 'pointer', color: '#64748b', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
                        </div>
                        <div className="modal-form-grid">
                            <div className="full">
                                <label className="contact-form-label">Document Title</label>
                                <div className="cd-input-wrap">
                                    <Icon d={icons.edit} size={18} className="cd-input-icon" />
                                    <input className="cd-input" value={documentForm.title || ''} onChange={e => setDocumentForm({ ...documentForm, title: e.target.value })} placeholder="e.g. Service Agreement 2024" />
                                </div>
                            </div>
                            <div>
                                <label className="contact-form-label">Document Type</label>
                                <select className="cd-select" value={documentForm.type || ''} onChange={e => setDocumentForm({ ...documentForm, type: e.target.value })} style={{ paddingLeft: 16 }}>
                                    <option value="">Select Type</option>
                                    {documentTypes.map(t => <option key={t._id} value={t.name}>{t.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="contact-form-label">Expiry Date (Optional)</label>
                                <div className="cd-input-wrap">
                                    <input className="cd-input" type="date" value={documentForm.expiryDate ? new Date(documentForm.expiryDate).toISOString().split('T')[0] : ''} onChange={e => setDocumentForm({ ...documentForm, expiryDate: e.target.value })} style={{ paddingLeft: 16 }} />
                                </div>
                            </div>
                            <div className="full">
                                <label className="contact-form-label">File Attachment</label>
                                <div className="file-upload-zone">
                                    <Icon d={icons.upload} size={20} className="file-upload-icon" />
                                    <span className="file-upload-text">{documentForm.fileName ? 'Change Document' : 'Click to Upload PDF/Image'}</span>
                                    <input type="file" onChange={handleDocumentFileChange} disabled={uploading} />
                                </div>
                                {documentForm.fileName && <p style={{ fontSize: 11, color: '#10b981', marginTop: 8, fontWeight: 700 }}>✓ {documentForm.fileName}</p>}
                            </div>
                        </div>
                        <div style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                            <button className="act-btn" style={{ flex: 'none', padding: '12px 24px', fontSize: 14 }} onClick={() => setShowDocumentModal(false)} disabled={saving}>Discard</button>
                            <button className="cd-btn-save" onClick={saveDocument} disabled={uploading || saving}>
                                {uploading ? 'Uploading...' : saving ? 'Saving...' : editingDocumentIdx !== null ? 'Update Document' : 'Save Document'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
